import React, { useEffect, useState, useCallback, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Save, RefreshCw, Shield, Building2, Users, UserCog, Crown, Loader2, Check } from "lucide-react";
import { useRolePermissions } from "@/hooks/rollenbeheer/useRolePermissions";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Company, companyLabels, Role, Resource, PermissionType, resourceLabels, Permission } from "@/hooks/rollenbeheer/types";
import { Switch } from "@/components/ui/switch";
import AuditLog from "@/components/rollenbeheer/AuditLog";

const roleConfig: Record<Role, { label: string; icon: React.ReactNode; description: string }> = {
  medewerker: {
    label: "Medewerker",
    icon: <Users className="h-4 w-4" />,
    description: "Standaard rechten voor medewerkers",
  },
  planner: {
    label: "Planner",
    icon: <UserCog className="h-4 w-4" />,
    description: "Uitgebreide rechten voor planners",
  },
  management: {
    label: "Management",
    icon: <Crown className="h-4 w-4" />,
    description: "Volledige toegang voor management",
  },
};

const permissionTypeLabels: Record<PermissionType, string> = {
  read: "Bekijken",
  write: "Bewerken",
  delete: "Verwijderen",
};

const Rollenbeheer = () => {
  const {
    permissions,
    isLoading,
    isDirty,
    updatePermission,
    savePermissions,
    initialized,
    databaseInitialized,
    lastSaveTime,
  } = useRolePermissions();

  const { user } = useAuth();
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState<"idle" | "saving" | "saved">("idle");
  const [selectedCompany, setSelectedCompany] = useState<Company>("luijten_vvz");
  const [selectedRole, setSelectedRole] = useState<Role>("medewerker");
  const autoSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (user && user.role !== "management") {
      toast.error("Alleen management heeft toegang tot rollenbeheer");
      navigate("/dashboard");
    }
  }, [user, navigate]);

  // Autosave: debounce 1.5s after last change
  useEffect(() => {
    if (!isDirty) return;

    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);

    autoSaveTimer.current = setTimeout(async () => {
      setAutoSaveStatus("saving");
      try {
        const success = await savePermissions();
        if (success) {
          setAutoSaveStatus("saved");
          setTimeout(() => setAutoSaveStatus("idle"), 2000);
        } else {
          setAutoSaveStatus("idle");
          toast.error("Automatisch opslaan mislukt");
        }
      } catch {
        setAutoSaveStatus("idle");
        toast.error("Automatisch opslaan mislukt");
      }
    }, 1500);

    return () => {
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    };
  }, [isDirty, permissions, savePermissions]);

  const handleSave = useCallback(async () => {
    if (!isDirty) {
      toast.info("Geen wijzigingen om op te slaan");
      return;
    }
    setIsSaving(true);
    try {
      const success = await savePermissions();
      if (success) {
        toast.success("Wijzigingen opgeslagen");
        setAutoSaveStatus("saved");
        setTimeout(() => setAutoSaveStatus("idle"), 2000);
      } else {
        toast.error("Opslaan mislukt");
      }
    } catch (error: any) {
      toast.error(`Fout bij opslaan: ${error?.message || "Onbekende fout"}`);
    } finally {
      setIsSaving(false);
    }
  }, [isDirty, savePermissions]);

  const handleRefresh = useCallback(() => {
    if (isDirty) {
      if (window.confirm("Er zijn niet-opgeslagen wijzigingen. Weet je zeker dat je wilt herladen?")) {
        window.location.reload();
      }
    } else {
      window.location.reload();
    }
  }, [isDirty]);

  const filteredPermissions = permissions
    .filter((p) => p.company === selectedCompany && p.role === selectedRole)
    .sort((a, b) => a.resource.localeCompare(b.resource));

  const handleToggle = (resource: Resource, type: PermissionType, current: boolean) => {
    updatePermission(selectedRole, resource, type, !current, selectedCompany);
  };

  // Toggle all permissions for a resource
  const allEnabled = (p: Permission) => p.read && p.write && p.delete;
  const handleToggleAll = (resource: Resource, permission: Permission) => {
    const enable = !allEnabled(permission);
    updatePermission(selectedRole, resource, "read", enable, selectedCompany);
    updatePermission(selectedRole, resource, "write", enable, selectedCompany);
    updatePermission(selectedRole, resource, "delete", enable, selectedCompany);
  };

  return (
    <div className="container mx-auto py-6 space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Shield className="h-7 w-7 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Rollenbeheer</h1>
            <p className="text-sm text-muted-foreground">
              Beheer toegangsrechten per bedrijf en rol
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Autosave status */}
          <span className="text-xs text-muted-foreground flex items-center gap-1.5">
            {autoSaveStatus === "saving" && (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Opslaan...
              </>
            )}
            {autoSaveStatus === "saved" && (
              <>
                <Check className="h-3.5 w-3.5 text-green-600" />
                Opgeslagen
              </>
            )}
            {autoSaveStatus === "idle" && isDirty && (
              <span className="text-amber-500">Niet opgeslagen</span>
            )}
          </span>
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={isLoading || isSaving}>
            <RefreshCw className="h-4 w-4 mr-1" />
            Herladen
          </Button>
        </div>
      </div>

      {/* Company selector */}
      <Tabs value={selectedCompany} onValueChange={(v) => setSelectedCompany(v as Company)}>
        <TabsList className="w-full sm:w-auto">
          {(Object.entries(companyLabels) as [Company, string][]).map(([key, label]) => (
            <TabsTrigger key={key} value={key} className="flex-1 sm:flex-none gap-1.5">
              <Building2 className="h-3.5 w-3.5" />
              {label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/* Role selector */}
      <div className="grid grid-cols-3 gap-3">
        {(Object.entries(roleConfig) as [Role, typeof roleConfig.medewerker][]).map(([role, config]) => {
          const isSelected = selectedRole === role;
          return (
            <button
              key={role}
              onClick={() => setSelectedRole(role)}
              className={`flex flex-col items-start gap-1 p-4 rounded-xl border-2 transition-all text-left ${
                isSelected
                  ? "border-primary bg-primary/5 shadow-sm"
                  : "border-border hover:border-primary/30 hover:bg-muted/50"
              }`}
            >
              <div className="flex items-center gap-2">
                <span className={isSelected ? "text-primary" : "text-muted-foreground"}>
                  {config.icon}
                </span>
                <span className={`font-semibold text-sm ${isSelected ? "text-primary" : ""}`}>
                  {config.label}
                </span>
              </div>
              <span className="text-xs text-muted-foreground">{config.description}</span>
            </button>
          );
        })}
      </div>

      {/* Permissions grid */}
      {isLoading ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            Rechten laden...
          </CardContent>
        </Card>
      ) : filteredPermissions.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            Geen rechten gevonden voor deze combinatie.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {filteredPermissions.map((permission) => (
            <Card key={permission.id} className="overflow-hidden">
              <div className="flex items-center justify-between p-4">
                <div className="flex items-center gap-3 min-w-0">
                  <div
                    className={`w-2 h-8 rounded-full flex-shrink-0 ${
                      allEnabled(permission)
                        ? "bg-green-500"
                        : permission.read || permission.write || permission.delete
                        ? "bg-amber-400"
                        : "bg-muted"
                    }`}
                  />
                  <div>
                    <p className="font-medium text-sm">{resourceLabels[permission.resource]}</p>
                    <button
                      onClick={() => handleToggleAll(permission.resource, permission)}
                      className="text-xs text-muted-foreground hover:text-primary transition-colors"
                    >
                      {allEnabled(permission) ? "Alles uitschakelen" : "Alles inschakelen"}
                    </button>
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  {(["read", "write", "delete"] as PermissionType[]).map((type) => (
                    <label
                      key={type}
                      className="flex flex-col items-center gap-1.5 cursor-pointer select-none"
                    >
                      <span className="text-[11px] text-muted-foreground font-medium">
                        {permissionTypeLabels[type]}
                      </span>
                      <Switch
                        checked={permission[type]}
                        onCheckedChange={() => handleToggle(permission.resource, type, permission[type])}
                      />
                    </label>
                  ))}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Audit log */}
      <AuditLog />

      {/* Status footer */}
      {lastSaveTime && (
        <p className="text-xs text-muted-foreground text-center">
          Laatst opgeslagen: {lastSaveTime}
        </p>
      )}
    </div>
  );
};

export default Rollenbeheer;
